import React, { useState, useEffect, useRef, SyntheticEvent } from 'react';
import classnames from 'classnames';
import ImageGallery from 'react-image-gallery';
import { useTranslation } from 'react-i18next';
import ArrowBackIos from '@material-ui/icons/ArrowBackIos';
import { useModal } from 'hooks/useModal';
import { MorphologyType } from 'types/ProductPageV2/Products';

import 'react-image-gallery/styles/scss/image-gallery.scss';
import styles from './ProductGallery.module.scss';

/**
 * ProductGallery for Desktop
 */
function ProductGalleryDesktop({
  data,
  selectedMorphologyType,
  onSlideAction,
  disableZoomMode = false,
}: {
  /** List of images and videos (main images/videos and their thumbnails) */
  data: {
    srcSet: string;
    thumbnail: string;
    type?: string;
    morphology: MorphologyType;
  }[];
  selectedMorphologyType: (morphology: MorphologyType) => void;
  onSlideAction?: (direction: 'prev' | 'next', e?: SyntheticEvent) => void;
  disableZoomMode?: boolean;
}): JSX.Element | null {
  const imageGalleryRef = useRef(null);
  const { t } = useTranslation();
  const { disableBodyScroll, enableBodyScroll } = useModal();
  const [index, setIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const MAX_THUMBAILS = 6;

  function prevThumbnail(e: SyntheticEvent) {
    onSlideAction?.('prev', e);
    (imageGalleryRef?.current as ImageGallery)?.slideToIndex(
      (imageGalleryRef?.current as ImageGallery)?.getCurrentIndex() - 1
    );
  }

  function nextThumbnail(e: SyntheticEvent) {
    onSlideAction?.('next', e);
    (imageGalleryRef?.current as ImageGallery)?.slideToIndex(
      (imageGalleryRef?.current as ImageGallery)?.getCurrentIndex() + 1
    );
  }

  function renderFullscreenButton(onClick, isFullscreen) {
    return (
      <>
        {isFullscreen && (
          <button type="button" className={styles.close} onClick={onClick} />
        )}

        {isFullscreen && data.length >= MAX_THUMBAILS && (
          <>
            {index !== 0 && (
              <button
                onClick={prevThumbnail}
                className={classnames(styles.arrowPrevThumbnail, {
                  [styles.arrowPrevFullscreen]: isFullscreen,
                })}
                aria-label={t('LABEL_PREVIOUS')}
              >
                <ArrowBackIos />
              </button>
            )}
            {index !== data.length - 1 && (
              <button
                onClick={nextThumbnail}
                className={classnames(styles.arrowNextThumbnail, {
                  [styles.arrowNextFullscreen]: isFullscreen,
                })}
                aria-label={t('LABEL_NEXT')}
              >
                <ArrowBackIos />
              </button>
            )}
          </>
        )}
      </>
    );
  }

  useEffect(() => {
    setIndex(0); // Reinitialize the index position when the data change (ex: switch colors or slim / curvy)
    setTimeout(() => {
      const galleryEl = imageGalleryRef?.current as ImageGallery;
      const galleryHeight = galleryEl?.state.thumbnailsWrapperHeight;
      const thumbsContainer = galleryEl?.imageGallery?.current?.querySelectorAll(
        '.image-gallery-thumbnails'
      )[0];

      thumbsContainer?.style.setProperty(
        'height',
        `${galleryHeight}px`,
        'important'
      );
    }, 750); // have to wait 750ms animation time / slideDuration by default is 450
  }, [data]);

  useEffect(() => {
    return () => {
      enableBodyScroll();
    };
  }, []);

  return (
    <>
      {!isFullscreen && data.length >= MAX_THUMBAILS && (
        <>
          {index !== 0 && (
            <button
              onClick={prevThumbnail}
              className={classnames(
                styles.arrowPrevThumbnail,
                'image-gallery-icon image-gallery-left-nav',
                { [styles.arrowPrevFullscreen]: isFullscreen }
              )}
              aria-label={t('LABEL_PREVIOUS')}
            >
              <ArrowBackIos />
            </button>
          )}
          {index !== data.length - 1 && (
            <button
              onClick={nextThumbnail}
              className={classnames(
                styles.arrowNextThumbnail,
                'image-gallery-icon image-gallery-right-nav',
                { [styles.arrowNextFullscreen]: isFullscreen }
              )}
              aria-label={t('LABEL_NEXT')}
            >
              <ArrowBackIos />
            </button>
          )}
        </>
      )}
      <ImageGallery
        ref={imageGalleryRef}
        additionalClass={styles.customRoot}
        items={data}
        thumbnailPosition={'left'}
        showPlayButton={false}
        renderFullscreenButton={renderFullscreenButton}
        onScreenChange={isFullScreen => {
          setIsFullscreen(isFullScreen);
          isFullScreen ? disableBodyScroll() : enableBodyScroll();
        }}
        useBrowserFullscreen={false}
        onBeforeSlide={nextIndex => setIndex(nextIndex)}
        onClick={e => {
          if (disableZoomMode) return;

          const sourceElement = e.target as HTMLElement;

          if (sourceElement.closest('.wrapper-video') && isFullscreen) {
            return;
          }

          !isFullscreen
            ? (imageGalleryRef?.current as ImageGallery)?.fullScreen()
            : (imageGalleryRef?.current as ImageGallery)?.exitFullScreen();
        }}
        onSlide={index => selectedMorphologyType(data[index]?.morphology)}
      />
    </>
  );
}

export default ProductGalleryDesktop;
